import { useEffect, useState } from 'react';
import type {
  CancellationRule,
  DepositRule,
  NoShowRule,
  PolicyRule,
  Service,
} from '@salonomy/types';
import { Button, Card, Pill, Spinner, Toggle } from '@salonomy/ui';
import {
  useBookingPolicy,
  useServicePolicyOverride,
  useSetBookingPolicy,
} from '../../data/policy.js';
import { useServices } from '../../data/services.js';
import { useToast } from '../../lib/toast.js';
import { CancellationRuleFields, DepositRuleFields, NoShowRuleFields } from './PolicyRuleFields.js';
import { PolicyTextPreview } from './PolicyTextPreview.js';
import { ServicePolicyOverrideModal } from './ServicePolicyOverrideModal.js';
import { defaultPolicyRule } from './policyText.js';

/**
 * BookingPolicyEditor (WS7 phase 2) — fills the "Booking policies" section reserved in brief 06's
 * settings. Per-salon money rules (deposit / cancellation window / no-show fee) configured once,
 * with per-service overrides; a live plain-English preview of EXACTLY what a client sees; and the
 * arm-auto-charge opt-in (default OFF) framed "we suggest, you confirm".
 *
 * RBAC: manager-only writes mirror the API (PUT /policy is requireManager). The host (SettingsPage)
 * only renders this in the manager branch, but we accept `canEdit` to disable controls defensively —
 * a chair-renter must never get a server 403 as their first feedback.
 */
export function BookingPolicyEditor({ canEdit = true }: { canEdit?: boolean }) {
  const toast = useToast();
  const { data: policy, isLoading } = useBookingPolicy();
  const { data: services } = useServices();
  const setPolicy = useSetBookingPolicy();

  // Working copy of the salon rule, seeded from the server policy and re-seeded whenever it changes.
  const [deposit, setDeposit] = useState<DepositRule>(defaultPolicyRule().deposit);
  const [cancellation, setCancellation] = useState<CancellationRule>(
    defaultPolicyRule().cancellation,
  );
  const [noShow, setNoShow] = useState<NoShowRule>(defaultPolicyRule().noShow);
  const [armAutoCharge, setArmAutoCharge] = useState(false);
  const [overrideService, setOverrideService] = useState<Service | null>(null);

  useEffect(() => {
    if (policy) {
      setDeposit(policy.rule.deposit);
      setCancellation(policy.rule.cancellation);
      setNoShow(policy.rule.noShow);
      setArmAutoCharge(policy.rule.armAutoCharge);
    }
  }, [policy]);

  const draft: PolicyRule = { deposit, cancellation, noShow, armAutoCharge };

  // A fee that could actually be auto-charged exists when a late-cancel or no-show fee is set. The
  // arm toggle is meaningless without one, so we explain that rather than silently no-op.
  const hasChargeableFee = cancellation.feeKind !== 'none' || noShow.feeKind !== 'none';

  const activeServices = (services ?? []).filter((s) => s.active);

  async function save() {
    if (!canEdit) return;
    try {
      await setPolicy.mutateAsync({ deposit, cancellation, noShow, armAutoCharge });
      toast.success('Booking policy saved');
    } catch {
      toast.error('Could not save the booking policy.');
    }
  }

  return (
    <Card>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
      >
        <div>
          <div className="section-title">Booking policies</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            Deposits, cancellation windows and no-show fees. Set once for the whole salon, with
            per-service exceptions. Clients see these terms before they book.
          </div>
        </div>
        {isLoading ? <Spinner /> : <Pill tone="neutral">Brief 07</Pill>}
      </div>

      {isLoading ? null : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 12 }}>
          <fieldset
            disabled={!canEdit}
            style={{ border: 0, padding: 0, margin: 0, minInlineSize: 'auto' }}
          >
            <DepositRuleFields rule={deposit} onChange={setDeposit} />
            <CancellationRuleFields rule={cancellation} onChange={setCancellation} />
            <NoShowRuleFields rule={noShow} onChange={setNoShow} />
          </fieldset>

          {/* ── Arm auto-charge — opt-in, default OFF, "we suggest, you confirm" ── */}
          <div
            style={{
              border: '1px solid var(--slm-border)',
              borderRadius: 10,
              padding: '12px 14px',
              marginTop: 10,
              background: armAutoCharge ? 'var(--slm-porcelain)' : undefined,
            }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Toggle
                checked={armAutoCharge}
                onChange={setArmAutoCharge}
                disabled={!canEdit}
                label="Arm automatic charging of fees"
                labelHidden
              />
              <span style={{ fontWeight: 600, fontSize: 14 }}>
                Charge late-cancel and no-show fees automatically
              </span>
              <Pill tone={armAutoCharge ? 'cobalt' : 'neutral'}>
                {armAutoCharge ? 'Armed' : 'Off'}
              </Pill>
            </label>
            <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.55, marginTop: 8 }}>
              We suggest, you confirm. Left off, we flag a late cancellation or no-show and a fee
              for you to take with one tap — we never take money on automation you didn’t arm.
              Switch it on and we’ll charge the fee above automatically, always against the exact
              policy the client agreed to when they booked.
            </div>
            {armAutoCharge && !hasChargeableFee ? (
              <div
                className="muted"
                style={{ fontSize: 12.5, marginTop: 8, color: 'var(--slm-danger)' }}
              >
                There’s no late-cancel or no-show fee set, so nothing will be charged. Set a fee
                above to make this do anything.
              </div>
            ) : null}
          </div>

          <div style={{ marginTop: 12 }}>
            <PolicyTextPreview rule={draft} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <Button onClick={save} disabled={!canEdit || setPolicy.isPending}>
              Save booking policy
            </Button>
          </div>

          {/* ── Per-service overrides ── */}
          <div className="section-title" style={{ fontSize: 15, margin: '16px 0 2px' }}>
            Per-service exceptions
          </div>
          <div className="muted" style={{ fontSize: 12.5, marginBottom: 4 }}>
            Most services follow the salon policy. Override individual ones where they differ.
          </div>
          {activeServices.length === 0 ? (
            <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
              Add services to set per-service exceptions.
            </div>
          ) : (
            <div className="settings-list">
              {activeServices.map((s) => (
                <ServiceOverrideRow
                  key={s.id}
                  service={s}
                  canEdit={canEdit}
                  onEdit={() => setOverrideService(s)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {overrideService && policy ? (
        <ServicePolicyOverrideModal
          service={overrideService}
          salonRule={policy.rule}
          onClose={() => setOverrideService(null)}
        />
      ) : null}
    </Card>
  );
}

/** One service row in the exceptions list, badged with whether it overrides the salon policy. */
function ServiceOverrideRow({
  service,
  canEdit,
  onEdit,
}: {
  service: Service;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const { data: override } = useServicePolicyOverride(service.id);
  return (
    <div className="settings-row">
      <div>
        <div style={{ fontWeight: 600 }}>{service.name}</div>
        <div className="muted" style={{ fontSize: 12.5 }}>
          {override ? 'Custom policy for this service' : 'Follows the salon policy'}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {override ? <Pill tone="cobalt">Override</Pill> : <Pill tone="soft">Default</Pill>}
        <Button size="sm" variant="ghost" onClick={onEdit} disabled={!canEdit}>
          {override ? 'Edit' : 'Override'}
        </Button>
      </div>
    </div>
  );
}
